import type React from "react";
import { useState, useEffect } from "react";
import { X } from "lucide-react";
import { formatPermissionLabel } from "../../utils/utils";
import useApiHook from "../../utils/useApi";
import type {
  AddRoleInterface,
  RoleApiResponse,
} from "../../interface/interfaces";
import { toast } from "react-toastify";
import { useRoleContext } from "../../context/RolesContext";

interface CustomRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  permissions: string[];
}

interface PermissionCategory {
  title: string;
  permissions: string[];
}

const EditRoleModal: React.FC<CustomRoleModalProps> = ({
  isOpen,
  onClose,
  permissions,
}) => {
  const { activeRole } = useRoleContext();

  const { data: roleData, makeRequest: roleReq } = useApiHook<RoleApiResponse>(
    "/admin/roles",
    {
      method: "get",
    }
  );

  useEffect(() => {
    roleReq({ page: 1, size: 1000 });
  }, []);

  const [originalRoleName, setOriginalRoleName] = useState("");
  const [originalRoleDescription, setOriginalRoleDescription] = useState("");
  const [originalPermissions, setOriginalPermissions] = useState<string[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (roleData?.data?.data && activeRole) {
      const selectedRole = roleData.data.data.find(
        (role) => role.roleReference === activeRole
      );

      if (selectedRole) {
        const name = selectedRole.roleName || "";
        const description = selectedRole.roleDescription || "";
        const permissions =
          selectedRole.rolePermissions &&
          Array.isArray(selectedRole.rolePermissions)
            ? selectedRole.rolePermissions
            : [];

        // Set current values
        setRoleName(name);
        setRoleDescription(description);
        setSelectedPermissions(permissions);

        // Set original values for comparison
        setOriginalRoleName(name);
        setOriginalRoleDescription(description);
        setOriginalPermissions([...permissions]);
      }
    }
  }, [roleData, activeRole]);

  const [roleName, setRoleName] = useState("");
  const [roleDescription, setRoleDescription] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [permissionCategories, setPermissionCategories] = useState<
    PermissionCategory[]
  >([]);

  const { isLoading, makeRequest } = useApiHook<AddRoleInterface>(
    `/admin/update-role`,
    {
      method: "post",
    }
  );

  useEffect(() => {
    const categories: Record<string, string[]> = {};

    permissions.forEach((permission) => {
      const category = getCategoryFromPermission(permission);
      if (!categories[category]) {
        categories[category] = [];
      }
      categories[category].push(permission);
    });

    const formattedCategories = Object.entries(categories).map(
      ([title, perms]) => ({
        title,
        permissions: perms,
      })
    );

    setPermissionCategories(formattedCategories);
  }, [permissions]);

  useEffect(() => {
    // Check if any values have changed from their original state
    const nameChanged = roleName !== originalRoleName;
    const descriptionChanged = roleDescription !== originalRoleDescription;

    // Check if permissions have changed (added or removed)
    const permissionsChanged = !(
      selectedPermissions.length === originalPermissions.length &&
      selectedPermissions.every((perm) => originalPermissions.includes(perm)) &&
      originalPermissions.every((perm) => selectedPermissions.includes(perm))
    );

    setHasChanges(nameChanged || descriptionChanged || permissionsChanged);
  }, [
    roleName,
    roleDescription,
    selectedPermissions,
    originalRoleName,
    originalRoleDescription,
    originalPermissions,
  ]);

  const getCategoryFromPermission = (permission: string): string => {
    if (permission.includes("TRANSACTION")) return "Transactions";
    if (permission.includes("USER")) return "Users";
    if (permission.includes("CUSTOMER")) return "Customers";
    if (permission.includes("SETTLEMENT")) return "Settlements";
    if (permission.includes("ROLE")) return "Roles";
    if (permission.includes("INVITE")) return "Invitations";
    return "Other";
  };

  const handlePermissionChange = (permission: string) => {
    setSelectedPermissions((prev) => {
      if (prev.includes(permission)) {
        return prev.filter((p) => p !== permission);
      } else {
        return [...prev, permission];
      }
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const merchantId = localStorage.getItem("active");

    const payload = {
      roleName: roleName.toUpperCase(),
      roleDescription,
      roleReference: activeRole,
      isAdminRole: false,
      isSystemRole: false,
      isCustomRole: true,
      rolePermissions: selectedPermissions,
    };

    const response = await makeRequest(payload);

    if (response?.status === 200 || response?.status === 201) {
      toast("Role updated successfully");

      setRoleName("");
      setRoleDescription("");
      setSelectedPermissions([]);

      onClose();

      window.location.reload();
    } else {
      // Error case
      toast.warning("Update Failed");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto your-scroll-container">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-medium">Update Custom Role</h2>
          <button
            onClick={onClose}
            type="button"
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-4 space-y-4">
            <div>
              <label
                htmlFor="roleName"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Role Name
              </label>
              <input
                type="text"
                id="roleName"
                name="roleName"
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                placeholder="Name Your Role"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label
                htmlFor="roleDescription"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Role Description
              </label>
              <input
                type="text"
                id="roleDescription"
                name="roleDescription"
                value={roleDescription}
                onChange={(e) => setRoleDescription(e.target.value)}
                placeholder="Describe Your Role"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <fieldset>
              <legend className="sr-only">Permissions</legend>
              {permissionCategories.map((category) => (
                <div key={category.title} className="space-y-2">
                  <h3 className="font-medium text-gray-700">
                    {category.title}
                  </h3>
                  <div className="space-y-1">
                    {category.permissions.map((permission) => (
                      <div key={permission} className="flex items-center">
                        <input
                          type="checkbox"
                          id={permission}
                          name="permissions"
                          value={permission}
                          checked={selectedPermissions.includes(permission)}
                          onChange={() => handlePermissionChange(permission)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label
                          htmlFor={permission}
                          className="ml-2 text-sm text-gray-700"
                        >
                          {formatPermissionLabel(permission)}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </fieldset>
          </div>

          <div className="flex justify-end gap-2 p-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                isLoading ||
                !roleName ||
                !roleDescription ||
                selectedPermissions.length === 0 ||
                !hasChanges
              }
              className={`px-4 py-2 text-sm text-white rounded-md transition-colors flex items-center justify-center ${
                !roleName ||
                !roleDescription ||
                selectedPermissions.length === 0 ||
                isLoading ||
                !hasChanges
                  ? "bg-blue-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Updating...
                </>
              ) : (
                "Update Role"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditRoleModal;
